import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getMatchEquationText } from "../../../helper/helper";
import { getPlayoffLabel } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/matchDetailDisplay";
import { IcPlay, IcShare } from "../../tournamentPage/icons";
import "./liveMatch.scss";
import "./liveStats.scss";
import CommentaryTab from "./CommentaryTab";
import CurrentBatters from "./CurrentBatters";
import { creaseBatters } from "./creaseBatters";
import InfoTab from "./InfoTab";
import LiveStatsTab from "./LiveStatsTab";
import OversTab from "./OversTab";
import ScorecardTab from "./ScorecardTab";
import SquadsTab from "./SquadsTab";
import { projectedScore, batterName, bowlerName, econOf, enumLabel, fmtDate, fmtTime, fullName, inningOrdinal, isDismissed, isMultiDayFormat, isLegalBall, legalBallsOf, matchRows, norm, oversText, scoreText, sortBalls, srOf, teamByName, teamRefs, venueShort, winProbability } from "./helpers";
import type { MatchInfo } from "./helpers";
import { BallRow, Chip, Crest, Empty, PlayerLink, ViewsBadge } from "./parts";
import { useInningCommentary, useLiveMatch } from "./useLiveMatch";
import { useLiveStats, useProgression } from "./useLiveStats";
import MatchVideos from "../media/MatchVideos";
import { useMatchMedia } from "../media/useMatchMedia";

const TABS = [
  ["live", "Live"],
  ["stats", "Live stats"],
  ["scorecard", "Scorecard"],
  ["overs", "Overs"],
  ["commentary", "Commentary"],
  ["squads", "Squads"],
  ["info", "Info"],
] as const;
type TabKey = (typeof TABS)[number][0];
type InningSel = Record<"scorecard" | "overs" | "commentary", number | null>;

const headerOffset = () => {
  if (typeof document === "undefined") return 0;
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string) => parseFloat(cs.getPropertyValue(name)) || 0;
  return v("--top-header-height") + v("--sub-header-height");
};

const LiveMatchPage = ({ matchId }: { matchId: string }) => {
  const navigate = useNavigate();
  const { match, innings, liveIndex, isLive, squadOne, squadTwo } = useLiveMatch(matchId);
  const videos = useMatchMedia(matchId);
  const m: MatchInfo | undefined = match.data;
  const [tab, setTab] = useState<TabKey>("live");
  const [sel, setSel] = useState<InningSel>({ scorecard: null, overs: null, commentary: null });
  const [stuck, setStuck] = useState(false);
  const teamsRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const hasMatch = !!m;

  const liveInning = innings[liveIndex];
  const liveInningId = liveInning?.inningId;
  const latest = useInningCommentary(matchId, liveInningId, isLive, hasMatch);
  // a Test innings is large, so only that innings' balls are fetched; limited-overs formats fetch both for the comparisons
  const liveStats = useLiveStats(matchId, { enabled: hasMatch && (tab === "live" || tab === "stats"), live: isLive, inningId: isMultiDayFormat(m) ? liveInningId : undefined });
  const progression = useProgression(matchId, { enabled: hasMatch && tab === "stats", live: isLive });
  const liveBalls = liveStats.data?.innings.find((i) => i.inningId === liveInningId)?.balls ?? [];

  useEffect(() => {
    const el = teamsRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: `-${headerOffset()}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMatch]);

  const go = (k: TabKey, inning?: number) => {
    if (inning != null && (k === "scorecard" || k === "overs" || k === "commentary")) setSel((s) => ({ ...s, [k]: inning }));
    setTab(k);
    const bar = tabsRef.current;
    if (bar) {
      const top = bar.getBoundingClientRect().top + window.scrollY - headerOffset();
      if (window.scrollY > top) window.scrollTo({ top });
    }
  };

  const link = (e: MouseEvent, to: string) => {
    e.preventDefault();
    navigate(to);
  };

  const ctx = useMemo(() => {
    const inn = liveInning;
    const batters = inn?.battingScoreCardResponseList ?? [];
    const bowlers = inn?.bowlingScoreCardResponses ?? [];
    const strikerId = inn?.striker?.playerId;
    const nonStrikerId = inn?.nonStriker?.playerId;
    const striker = batters.find((b) => b.status === "STRIKER") ?? batters.find((b) => strikerId && b.playerId === strikerId);
    const nonStriker = batters.find((b) => b.status === "NON_STRIKER") ?? batters.find((b) => nonStrikerId && b.playerId === nonStrikerId);
    const cur = sortBalls(inn?.currentOverBalls ?? []);
    const last = sortBalls(inn?.lastOverBalls ?? []);
    const inProgressOver = cur.length > 0;
    const overBalls = inProgressOver ? cur : last;
    const curBowlerId = cur[cur.length - 1]?.bowler?.playerId ?? last[last.length - 1]?.bowler?.playerId;
    const curBowler = (curBowlerId ? bowlers.find((b) => b.playerId === curBowlerId) : undefined) ?? (!overBalls.length ? bowlers[bowlers.length - 1] : undefined);
    const prevId = inProgressOver ? last[last.length - 1]?.bowler?.playerId : undefined;
    const prevBowler = prevId && prevId !== curBowler?.playerId ? bowlers.find((b) => b.playerId === prevId) : undefined;
    const dismissed = batters.filter(isDismissed);
    const lastOut = dismissed[dismissed.length - 1];
    const lastWicket = lastOut ? `${batterName(lastOut)} ${(lastOut.outString ?? "").trim()} ${lastOut.run ?? 0} (${lastOut.balls ?? 0})`.replace(/\s+/g, " ") : "";

    const used = legalBallsOf(inn);
    const maxOvers = inn?.maxOvers ?? 0;
    const isTest = (m?.matchType ?? "").toUpperCase().includes("TEST");
    const chase = !!inn && liveIndex >= 1 && !!innings[0] && maxOvers > 0 && !isTest;
    const target = chase ? (innings[0].runs ?? 0) + 1 : 0;
    const total = chase ? maxOvers * 6 : 0;
    const need = chase ? Math.max(0, target - (inn?.runs ?? 0)) : 0;
    const left = chase ? Math.max(0, total - used) : 0;
    const crrN = inn && Number(inn.currentRunrate) > 0 ? Number(inn.currentRunrate) : used ? ((inn?.runs ?? 0) * 6) / used : null;
    const rrrN = chase && left > 0 && need > 0 ? (need * 6) / left : null;
    return { batters, striker, nonStriker, cur, overBalls, inProgressOver, curBowler, prevBowler, lastWicket, used, chase, target, total, need, left, crr: crrN, rrr: rrrN };
  }, [liveInning, innings, liveIndex, m?.matchType]);

  const refs = teamRefs(m);
  const tournamentId = m?.tournament?.tournamentId ?? sessionStorage.getItem("tournamentId") ?? "";
  const leagueId = m?.leagueId ?? m?.tournament?.leagueId ?? "";
  const leagueName = m?.leagueName ?? m?.tournament?.leagueName ?? "League";
  const tournamentName = m?.tournament?.tournamentName?.trim() || m?.leagueName || "Tournament";
  const tPath = tournamentId ? `/tournamentDetails/${encodeURIComponent(tournamentId)}` : "";
  const leaguePath = leagueId ? `/my-leagues/${encodeURIComponent(leagueId)}` : "/leagues-home";

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${refs[0]?.name} vs ${refs[1]?.name}`, url: window.location.href });
      else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied");
      }
    } catch {
      /* cancelled */
    }
  };

  /* ---------- loading / error ---------- */
  if (!m) {
    return (
      <div className="ml">
        <div className="ml-hero">
          <section className="hero">
            <div className="hbg" aria-hidden="true">
              <span className="halo" />
              <span className="tile" />
              <span className="scrim-l" />
              <span className="scrim-b" />
              <span className="grain" />
            </div>
            <div className="wrap">
              <div className="hero-state">
                {match.isLoading ? (
                  <>
                    <h1>Loading match…</h1>
                    <span className="sk" style={{ width: 260, background: "rgba(255,255,255,.14)" }} />
                  </>
                ) : (
                  <>
                    <h1>We could not load this match</h1>
                    <p>Check your connection and try again.</p>
                    <p style={{ marginTop: 14 }}>
                      <button type="button" className="hbtn solid" onClick={() => void match.refetch()}>
                        Retry
                      </button>
                    </p>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
        <div className="wrap main">
          <div className="state">{match.isLoading ? "Fetching the latest score…" : "Match details are unavailable right now."}</div>
        </div>
      </div>
    );
  }

  /* ---------- hero data ---------- */
  const rows = refs
    .map((t, ti) => {
      const inns = innings.map((x, i) => ({ x, i })).filter(({ x }) => norm(x.battingTeamName) === norm(t.name));
      return { t, inns, order: inns.length ? inns[0].i : 99 + ti };
    })
    .sort((a, b) => a.order - b.order);
  const status = m.matchStatus;
  const equation = getMatchEquationText({ ...m, inningScorecardList: innings });
  const wp = winProbability(m, innings);
  const proj = wp && isLive ? projectedScore(m, innings, liveIndex) : null;
  const venue = venueShort(m);
  const playoff = getPlayoffLabel(m.playoffType);
  const umpires = [m.umpire ?? m.availableUmpireResponse, m.umpireTwo].map((u) => (u ? fullName((u as { playerFirstName?: string }).playerFirstName, (u as { playerLastName?: string }).playerLastName) || (u as { name?: string }).name || "" : "")).filter(Boolean);
  const facts: [string, string][] = ([
    ["Toss", (m.tossResult ?? "").trim()],
    ["Last wicket", isLive ? ctx.lastWicket : ""],
    ["Format", [enumLabel(m.matchType), m.matchOvers && !isMultiDayFormat(m) ? `${m.matchOvers} overs` : ""].filter(Boolean).join(" · ")],
    ["Umpires", umpires.join(", ")],
  ] as [string, string][]).filter(([, v]) => !!v);
  // streamURL is the MediaLive ingest address, not something a viewer can open
  const watchUrl = videos.find((v) => v.type === "LIVE_STREAM")?.url || (m.youtubeStreamUrl ?? "").trim();
  const crumbTeams = `${refs[0]?.name || "Team 1"} vs ${refs[1]?.name || "Team 2"}`;
  const liveTeam = teamByName(m, liveInning?.battingTeamName);
  const showWp = !!wp || (isLive && ctx.chase);
  const innNum = liveInning?.currentTeamInningNumber ?? liveIndex + 1;
  const updated = match.dataUpdatedAt ? fmtTime(new Date(match.dataUpdatedAt).toISOString()) : "";

  const latestBalls = latest.overs.flatMap((o) => sortBalls(o.ballsResponseList ?? []).reverse().map((b) => ({ b, o: o.overNumber }))).slice(0, 8);
  const first = innings[0];
  const showFirst = isLive && liveIndex >= 1 && !!first;
  const firstTeam = teamByName(m, first?.battingTeamName);
  const topBat = showFirst ? [...(first.battingScoreCardResponseList ?? [])].sort((a, b) => (b.run ?? 0) - (a.run ?? 0)).slice(0, 3) : [];
  const topBowl = showFirst ? [...(first.bowlingScoreCardResponses ?? [])].sort((a, b) => (b.wickets ?? 0) - (a.wickets ?? 0) || (a.runs ?? 0) - (b.runs ?? 0)).slice(0, 3) : [];
  const railInfo = matchRows(m);

  const pendCount = ctx.inProgressOver ? Math.max(0, 6 - ctx.cur.filter(isLegalBall).length) : 0;
  const overRuns = ctx.overBalls.length ? ctx.overBalls[ctx.overBalls.length - 1]?.currentOverRuns ?? ctx.overBalls.reduce((a, b) => a + (b.runs_scored ?? 0), 0) : 0;
  const overNo = ctx.inProgressOver ? (liveInning?.over ?? 0) + 1 : liveInning?.over ?? 0;
  const overBowlerName = ctx.overBalls.length ? fullName(ctx.overBalls[ctx.overBalls.length - 1]?.bowler?.playerFirstName, ctx.overBalls[ctx.overBalls.length - 1]?.bowler?.playerLastName) : "";

  return (
    <div className="ml">
      <div className="ml-hero">
        <section className="hero">
          <div className="hbg" aria-hidden="true">
            <span className="halo" />
            <span className="tile" />
            <span className="scrim-l" />
            <span className="scrim-b" />
            <span className="grain" />
          </div>
          <div className="wrap">
            <nav className="crumb" aria-label="Breadcrumb" style={{ paddingTop: 18 }}>
              <a href={leaguePath} onClick={(e) => link(e, leaguePath)}>
                {leagueName}
              </a>
              <span>/</span>
              {tPath ? (
                <>
                  <a href={tPath} onClick={(e) => link(e, tPath)}>
                    {tournamentName}
                  </a>
                  <span>/</span>
                  <a href={`${tPath}?tab=matches`} onClick={(e) => link(e, `${tPath}?tab=matches`)}>
                    Matches
                  </a>
                  <span>/</span>
                </>
              ) : null}
              <b>{crumbTeams}</b>
            </nav>
            <div className="hgrid">
              <div>
                <div className="meta">
                  {isLive ? (
                    <span className="badge lv">
                      <span className="ldot" />
                      LIVE{innings.length ? ` · ${inningOrdinal(innNum - 1).toUpperCase()}` : ""}
                    </span>
                  ) : (
                    <span className="badge">{enumLabel(status).toUpperCase()}</span>
                  )}
                  {playoff ? <span className="badge">{playoff.toUpperCase()}</span> : null}
                  {[fmtDate(m.startDate), fmtTime(m.startDate)].filter(Boolean).join(" · ")}
                  {m.matchType ? (
                    <>
                      <i />
                      {enumLabel(m.matchType)}
                    </>
                  ) : null}
                  {venue ? (
                    <>
                      <i />
                      {venue}
                    </>
                  ) : null}
                  <ViewsBadge count={m.viewCount} />
                </div>
                <h1>{tournamentName}</h1>
                <div className="teams" ref={teamsRef}>
                  {rows.map(({ t, inns }) => {
                    const batting = isLive && inns.some(({ i }) => i === liveIndex);
                    const latestInn = inns[inns.length - 1]?.x;
                    return (
                      <div key={t.name} className={`trow ${isLive && !batting && inns.length ? "lost" : ""}`}>
                        <Crest team={t} />
                        <div className="nm">
                          <span>{t.name || "-"}</span>
                          {batting ? (
                            <span className="batpill">BATTING</span>
                          ) : inns.length ? (
                            <span className="batpill" style={{ background: "rgba(255,255,255,.08)", color: "#C9C8E6" }}>
                              {inningOrdinal(inns[0].i).toUpperCase()}
                            </span>
                          ) : (
                            <span className="batpill" style={{ background: "rgba(255,255,255,.08)", color: "#C9C8E6" }}>
                              YET TO BAT
                            </span>
                          )}
                        </div>
                        {inns.length ? (
                          <div className="sc">
                            {inns.map(({ x }) => scoreText(x)).join(" & ")}
                            <small>{oversText(latestInn)} ov</small>
                          </div>
                        ) : (
                          <div className="sc">
                            <span className="yb">–</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {equation && equation !== "-" ? (
                  <div className="result">
                    <span className="lvx">{equation}</span>
                    {isLive && ctx.crr != null ? (
                      <span>
                        CRR {ctx.crr.toFixed(2)}
                        {ctx.rrr != null ? ` · RRR ${ctx.rrr.toFixed(2)}` : ""}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {isLive && (ctx.striker || ctx.nonStriker || ctx.curBowler) ? (
                  <div className="hcrease">
                    {ctx.striker ? (
                      <span>
                        <span className="tg s">STRIKER</span>
                        <b>
                          <PlayerLink id={ctx.striker.playerId} name={batterName(ctx.striker)} />
                        </b>{" "}
                        <span className="n">{ctx.striker.run ?? 0}*</span> ({ctx.striker.balls ?? 0})
                      </span>
                    ) : null}
                    {ctx.nonStriker ? (
                      <span>
                        <span className="tg">NON-STRIKER</span>
                        <b>
                          <PlayerLink id={ctx.nonStriker.playerId} name={batterName(ctx.nonStriker)} />
                        </b>{" "}
                        <span className="n">{ctx.nonStriker.run ?? 0}*</span> ({ctx.nonStriker.balls ?? 0})
                      </span>
                    ) : null}
                    {ctx.curBowler ? (
                      <span>
                        <span className="tg">BOWLING</span>
                        <b>
                          <PlayerLink id={ctx.curBowler.playerId} name={bowlerName(ctx.curBowler)} />
                        </b>{" "}
                        <span className="n">
                          {ctx.curBowler.wickets ?? 0}/{ctx.curBowler.runs ?? 0}
                        </span>{" "}
                        ({ctx.curBowler.over})
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="hside">
                <div className="hacts">
                  <button type="button" className="hbtn" onClick={() => void share()}>
                    <IcShare />
                    Share
                  </button>
                  {watchUrl ? (
                    <a className="hbtn solid" href={watchUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <IcPlay />
                      Watch live
                    </a>
                  ) : null}
                </div>
                {showWp ? (
                  <div className="wpbox">
                    {wp ? (
                      <>
                        <div className="lbl">WIN PROBABILITY</div>
                        <div className="wpr">
                          <span>
                            <b>{wp[0]}%</b>
                            {refs[0]?.name}
                          </span>
                          <span>
                            {refs[1]?.name}
                            <b style={{ margin: "0 0 0 6px" }}>{wp[1]}%</b>
                          </span>
                        </div>
                        <div className="wpbar">
                          <i style={{ width: `${Math.min(100, Math.max(0, wp[0]))}%`, background: "#9FE3BF" }} />
                          <i style={{ flex: 1, background: "rgba(255,255,255,.28)" }} />
                        </div>
                        {proj ? (
                          <div className="wpproj">
                            <span>Projected score{proj.over ? ` · after ${proj.over} ov` : ""}</span>
                            <span><small>{liveTeam.name}</small><b>{proj.runs}</b></span>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    {isLive && ctx.chase ? (
                      <div className="st" style={wp ? undefined : { marginTop: 0, paddingTop: 0, borderTop: 0 }}>
                        <div>
                          <b>{ctx.target}</b>
                          <span>Target</span>
                        </div>
                        <div>
                          <b>{ctx.need}</b>
                          <span>Runs needed</span>
                        </div>
                        <div>
                          <b>{ctx.left}</b>
                          <span>Balls left</span>
                        </div>
                        <div>
                          <b>{Math.max(0, 10 - (liveInning?.wicket ?? 0))}</b>
                          <span>Wickets left</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            {facts.length ? (
              <div className="facts">
                {facts.map(([k, v]) => (
                  <div key={k}>
                    <div className="k">{k}</div>
                    <div className="v">{v}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <div className="ml-sticky" ref={tabsRef}>
        <div className="tabsbar">
          <div className="wrap">
            <div className={`mini ${stuck && isLive ? "on" : ""}`} aria-hidden="true">
              <span className="lv">
                <span className="ldot" />
                LIVE
              </span>
              <span className="t">
                <Crest team={liveTeam} size={32} />
                <span className="n">{scoreText(liveInning)}</span>
              </span>
              {ctx.chase ? <span className="t l">need {ctx.need} off {ctx.left}</span> : null}
            </div>
            <div className="tabs" role="tablist" aria-label="Match sections">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" className="tab" role="tab" id={`ml-tab-${k}`} aria-selected={tab === k} onClick={() => go(k)}>
                  {l}
                </button>
              ))}
            </div>
            {isLive && updated ? (
              <span className="updated">
                <span className="ldot" />
                Updated {updated} · refreshes every 30s
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="wrap main">
        <section className="pane on" role="tabpanel" aria-labelledby={`ml-tab-${tab}`}>
          {tab === "live" ? (
            <div className="cols" style={railInfo.length || showFirst ? undefined : { gridTemplateColumns: "minmax(0,1fr)" }}>
              <div className="col">
                <MatchVideos videos={videos} live={isLive} ended={false} />
                {!liveInning ? (
                  <Empty>{isLive ? "Waiting for the first ball of the match." : "Live data is not available for this match."}</Empty>
                ) : (
                  <>
                    <div className="box crease">
                      <div className="box-h">
                        <h3>At the crease</h3>
                        <div className="r">
                          <button type="button" className="link" onClick={() => go("scorecard")}>
                            Full scorecard
                          </button>
                        </div>
                      </div>
                      {ctx.striker || ctx.nonStriker ? (
                        <div className="tw">
                          <table>
                            <thead>
                              <tr>
                                <th className="l">Batter</th>
                                <th>R</th>
                                <th>B</th>
                                <th>4s</th>
                                <th>6s</th>
                                <th>SR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(
                                [
                                  [ctx.striker, "s", "STRIKER"],
                                  [ctx.nonStriker, "n", "NON-STRIKER"],
                                ] as const
                              )
                                .filter(([b]) => !!b)
                                .map(([b, c, l]) => (
                                  <tr key={b!.playerId}>
                                    <td className="t">
                                      <PlayerLink id={b!.playerId} name={batterName(b!)} />
                                      {c === "s" ? " *" : ""}
                                      <span className={`crole ${c}`}>{l}</span>
                                    </td>
                                    <td className="big">{b!.run ?? 0}</td>
                                    <td>{b!.balls ?? 0}</td>
                                    <td>{b!.fours ?? 0}</td>
                                    <td>{b!.six ?? 0}</td>
                                    <td>{b!.strikeRate != null ? Number(b!.strikeRate).toFixed(1) : srOf(b!.run, b!.balls)}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <Empty>Waiting for the next batter.</Empty>
                      )}
                      {ctx.overBalls.length ? (
                        <div className="thisov">
                          <span className="k">
                            {ctx.inProgressOver ? "THIS OVER" : "LAST OVER"}
                            <span>
                              Over {overNo}
                              {overBowlerName ? ` · ${overBowlerName}` : ""}
                            </span>
                          </span>
                          <span className="balls">
                            {ctx.overBalls.map((b, i) => (
                              <Chip key={b.ball_id ?? i} ball={b} />
                            ))}
                            {Array.from({ length: pendCount }).map((_, i) => (
                              <span key={`p${i}`} className="bl pend">
                                ·
                              </span>
                            ))}
                          </span>
                          <span className="sum">
                            <b>{overRuns}</b> runs off {ctx.overBalls.filter(isLegalBall).length} ball{ctx.overBalls.filter(isLegalBall).length === 1 ? "" : "s"}
                          </span>
                        </div>
                      ) : null}
                      {ctx.curBowler || ctx.prevBowler ? (
                        <div className="tw" style={{ borderTop: "1px solid var(--line)" }}>
                          <table>
                            <thead>
                              <tr>
                                <th className="l">Bowler</th>
                                <th>O</th>
                                <th>M</th>
                                <th>R</th>
                                <th>W</th>
                                <th>Econ</th>
                                <th>Dots</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                ctx.curBowler ? ([ctx.curBowler, ctx.inProgressOver ? "BOWLING" : "LAST OVER", "b"] as const) : null,
                                ctx.prevBowler ? ([ctx.prevBowler, "LAST OVER", "n"] as const) : null,
                              ]
                                .filter((x): x is NonNullable<typeof x> => !!x)
                                .map(([b, l, c]) => (
                                  <tr key={b.playerId}>
                                    <td className="t">
                                      <PlayerLink id={b.playerId} name={bowlerName(b)} />
                                      <span className={`crole ${c}`}>{l}</span>
                                    </td>
                                    <td>{b.over ?? 0}</td>
                                    <td>{b.maiden ?? 0}</td>
                                    <td className="big">{b.runs ?? 0}</td>
                                    <td className="big">{b.wickets ?? 0}</td>
                                    <td>{econOf(b)}</td>
                                    <td>{b.dotBall ?? 0}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {ctx.lastWicket ? (
                        <div className="box-f">
                          <b style={{ color: "var(--ink-2)" }}>Last wicket</b>
                          {ctx.lastWicket}
                        </div>
                      ) : null}
                    </div>

                    {creaseBatters(liveInning).length ? (
                      <div className="box">
                        <div className="box-h">
                          <h3>Current batters</h3>
                          <span className="sub">Every scoring shot this innings</span>
                          <div className="r">
                            <button type="button" className="link" onClick={() => go("stats")}>
                              More live stats
                            </button>
                          </div>
                        </div>
                        <CurrentBatters batters={creaseBatters(liveInning)} balls={liveBalls} live={isLive} loading={liveStats.isLoading} error={liveStats.isError} />
                      </div>
                    ) : null}

                    {ctx.chase ? (
                      <div className="box">
                        <div className="box-h">
                          <h3>The chase</h3>
                          <span className="sub">
                            Target {ctx.target} in {liveInning.maxOvers} overs
                          </span>
                        </div>
                        <div className="chase">
                          <div className="ln">
                            Runs
                            <span>
                              <b style={{ color: "var(--ink)" }}>{liveInning.runs ?? 0}</b> of {ctx.target}
                            </span>
                          </div>
                          <div className="bar">
                            <i style={{ width: `${Math.min(100, ((liveInning.runs ?? 0) / Math.max(1, ctx.target)) * 100)}%`, background: "var(--brand)" }} />
                          </div>
                          <div className="ln">
                            Balls used
                            <span>
                              <b style={{ color: "var(--ink)" }}>{ctx.used}</b> of {ctx.total}
                            </span>
                          </div>
                          <div className="bar">
                            <i style={{ width: `${Math.min(100, (ctx.used / Math.max(1, ctx.total)) * 100)}%`, background: "#9AA1AE" }} />
                          </div>
                        </div>
                        <div className="rrs">
                          <div>
                            <b>{ctx.crr != null ? ctx.crr.toFixed(2) : "-"}</b>
                            <span>Current run rate</span>
                          </div>
                          <div>
                            <b>{ctx.rrr != null ? ctx.rrr.toFixed(2) : "-"}</b>
                            <span>Required run rate</span>
                          </div>
                          <div>
                            <b>{ctx.need}</b>
                            <span>Runs needed</span>
                          </div>
                          <div>
                            <b>{ctx.left}</b>
                            <span>Balls left</span>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="box">
                      <div className="box-h">
                        <span className="innlbl lv">
                          {isLive ? <span className="ldot" /> : null}
                          {inningOrdinal(liveIndex)}
                        </span>
                        <h3>Latest balls</h3>
                        <span className="sub">Ball by ball, newest first</span>
                        <div className="r">
                          <button type="button" className="link" onClick={() => go("commentary")}>
                            Full commentary
                          </button>
                        </div>
                      </div>
                      {latest.isLoading ? (
                        <Empty>Loading the latest balls…</Empty>
                      ) : latestBalls.length ? (
                        <ul className="cm">
                          {latestBalls.map(({ b, o }, i) => (
                            <BallRow key={`${b.ball_id ?? i}-${o}`} ball={b} over={o} />
                          ))}
                        </ul>
                      ) : (
                        <Empty>No balls have been bowled yet.</Empty>
                      )}
                    </div>
                  </>
                )}
              </div>

              {railInfo.length || showFirst ? (
                <aside className="col rail">
                  {showFirst ? (
                    <div className="box">
                      <div className="box-h">
                        <span className="innlbl">1st innings</span>
                        <h3>{firstTeam.name || first.battingTeamName}</h3>
                      </div>
                      <div className="isum" style={{ gridTemplateColumns: "1fr" }}>
                        <div>
                          <div className="ih">
                            <Crest team={firstTeam} size={40} />
                            <b>{firstTeam.name || first.battingTeamName}</b>
                            <span className="n">
                              {scoreText(first)}
                              <small>{oversText(first)} ov</small>
                            </span>
                          </div>
                          {topBat.length ? <div className="ilbl">Top batters</div> : null}
                          {topBat.map((b) => (
                            <div className="prow2" key={b.playerId}>
                              <span className="nm">
                                <PlayerLink id={b.playerId} name={batterName(b)} />
                              </span>
                              <span className="v">
                                {b.run ?? 0}
                                {isDismissed(b) ? "" : "*"} <small>({b.balls ?? 0})</small>
                              </span>
                            </div>
                          ))}
                          {topBowl.length ? <div className="ilbl">Top bowlers · {teamByName(m, first.bowlingTeamName).name}</div> : null}
                          {topBowl.map((b) => (
                            <div className="prow2" key={b.playerId}>
                              <span className="nm">
                                <PlayerLink id={b.playerId} name={bowlerName(b)} />
                              </span>
                              <span className="v">
                                {b.wickets ?? 0}/{b.runs ?? 0} <small>({b.over})</small>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="box-f">
                        <button type="button" className="link" onClick={() => go("overs", 0)}>
                          1st innings overs
                        </button>
                        <button type="button" className="link" style={{ marginLeft: "auto" }} onClick={() => go("commentary", 0)}>
                          1st innings commentary
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {railInfo.length ? (
                    <div className="box">
                      <div className="box-h">
                        <h3>Match info</h3>
                      </div>
                      <dl className="dl">
                        {railInfo.map(([k, v]) => (
                          <div key={k} style={{ display: "contents" }}>
                            <dt>{k}</dt>
                            <dd>{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                </aside>
              ) : null}
            </div>
          ) : null}

          {tab === "stats" ? (
            <LiveStatsTab
              match={m}
              innings={innings}
              liveIndex={liveIndex}
              isLive={isLive}
              stats={liveStats.data}
              statsLoading={liveStats.isLoading}
              statsError={liveStats.isError}
              progression={progression.data}
              progressionLoading={progression.isLoading}
              curBowler={ctx.curBowler}
              prevBowler={ctx.prevBowler}
            />
          ) : null}
          {tab === "scorecard" ? <ScorecardTab match={m} innings={innings} liveIndex={liveIndex} matchLive={isLive} sel={sel.scorecard} setSel={(i) => setSel((s) => ({ ...s, scorecard: i }))} squadOne={squadOne.data} squadTwo={squadTwo.data} /> : null}
          {tab === "overs" ? <OversTab matchId={matchId} match={m} innings={innings} liveIndex={liveIndex} matchLive={isLive} sel={sel.overs} setSel={(i) => setSel((s) => ({ ...s, overs: i }))} /> : null}
          {tab === "commentary" ? <CommentaryTab matchId={matchId} match={m} innings={innings} liveIndex={liveIndex} matchLive={isLive} sel={sel.commentary} setSel={(i) => setSel((s) => ({ ...s, commentary: i }))} /> : null}
          {tab === "squads" ? <SquadsTab match={m} innings={innings} squads={[squadOne.data, squadTwo.data]} loading={squadOne.isLoading || squadTwo.isLoading} matchLive={isLive} /> : null}
          {tab === "info" ? <InfoTab match={m} /> : null}
        </section>
      </div>
    </div>
  );
};

export default LiveMatchPage;
